import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { CleanupFamilyTransfersButton } from "./CleanupFamilyTransfersButton";
import { backupRecordsToExcel } from "@/utils/countyResearchBackup";

interface BrewsterDocument {
  documentNumber: string;
  bookPage: string;
  grantors: string[];
  grantees: string[];
  docType: string;
  recordedDate: string;
  legalDescriptions: string[];
  lenderName: string;
}

interface Props {
  onRecordsSaved: () => void;
}

const CORPORATE_KEYWORDS = [
  'BANK', 'MORTGAGE', 'LENDING', 'CREDIT UNION', 'FINANCE', 'FUNDING',
  'LLC', 'LTD', 'INC', 'CORP', 'CORPORATION', 'FEDERAL', 'NATIONAL',
  'SERVICES', 'SOLUTIONS', 'CAPITAL', 'INVESTMENTS', 'HOLDINGS',
  'WELLS FARGO', 'CHASE', 'PENNYMAC', 'DHI', 'USAA', 'NAVY FEDERAL',
  'VETERANS UNITED', 'GUILD', 'CHURCHILL', 'FAIRWAY', 'NEWREZ',
  'BENCHMARK', 'SWBC', 'CANOPY', 'FLAGSTAR', 'FIDELITY',
  'FIRST NATIONAL', 'FIRST FINANCIAL', 'FIRST CONTINENTAL',
  'PLANET HOME', 'VILLAGE CAPITAL', 'ANGEL OAK',
  'UNITED WHOLESALE', 'SENTE', 'LOAN STORE',
  'HOUSING & URBAN', 'HUD', 'SECRETARY OF HOUSING',
  'MORTGAGE RESEARCH CENTER', 'MORTGAGE SOLUTIONS',
  'READYCAP', 'ROCKET MORTGAGE', 'PENNYMAC LOAN', 'PILGRIM MORTGAGE',
  'EFINITY FINANCIAL', 'CASCADE FINANCIAL', 'SOUTHWEST STAGE',
  'GUILD MORTGAGE', 'REVOLUTION MORTGAGE', 'CMG MORTGAGE', 'CMG HOME',
  'NBH BANK', 'FROST BANK', 'TEJAS BANK', 'MAVERICK BANK', 'TRANSPECOS BANK',
  'RED RIVER BANK', 'AMARILLO NATIONAL', 'STERLING BANK', 'COMMUNITY NATIONAL',
  'CEDAR RAPIDS BANK', 'SERVISFIRST BANK', 'EASY STREET CAPITAL',
  'CAPITAL FARM CREDIT', 'FARM CREDIT', 'WESTEX COMMUNITY CREDIT',
  'RANDOLPH-BROOKS', 'GULF COAST EDUCATORS', 'SELECT PORTFOLIO',
  'WILMINGTON TRUST', 'WILMINGTON SAVINGS', 'WESTCOR LAND TITLE',
  'FIRST AMERICAN TITLE', 'STEWART TITLE', 'PFN LENDING',
  'LEGACY MUTUAL MORTGAGE', 'INVESTOR MORTGAGE FINANCE',
  'FREEDOM MORTGAGE', 'NESTOR SOLUTIONS', 'JPMORGAN',
  'TITLE INSURANCE', 'GUARANTY COMPANY', 'VISIO FINANCIAL',
  'U S BANK', 'US BANK', 'AFFILIATED BANK', 'FORT DAVIS STATE BANK',
  'PECOS COUNTY STATE BANK', '21ST MORTGAGE', '21 MORTGAGE',
  'ALLIANT CREDIT', 'A FEDERAL CREDIT', 'GRANADA HOLDINGS',
  '1ST COMMUNITY FEDERAL', 'PENNYMAC', 'POLUNSKY',
];

export function BrewsterCountyTextParser({ onRecordsSaved }: Props) {
  const [textData, setTextData] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  const parseBrewsterData = (text: string): BrewsterDocument[] => {
    const lines = text.trim().split('\n');
    const documentMap = new Map<string, BrewsterDocument>();

    // Try to detect if data is tab-separated rows or vertical layout
    // Check if first non-empty, non-header line has tabs
    const isTabSeparated = lines.some(line => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('Doc#') || trimmed.startsWith('L:')) return false;
      const parts = trimmed.split('\t');
      return parts.length >= 4;
    });

    if (isTabSeparated) {
      return parseTabSeparated(lines);
    }

    return parseVerticalFormat(lines);
  };

  const parseTabSeparated = (lines: string[]): BrewsterDocument[] => {
    const documentMap = new Map<string, BrewsterDocument>();

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('Doc#')) continue;

      const parts = trimmed.split('\t').map(p => p.trim());

      // Check if this is a main record line (has a doc number in first column)
      const docNum = parts[0]?.match(/^\d{5,7}$/)?.[0];
      if (!docNum) {
        // Could be a legal description continuation line
        if (trimmed.startsWith('L:')) {
          // Find last document and append
          const lastKey = Array.from(documentMap.keys()).pop();
          if (lastKey) {
            documentMap.get(lastKey)!.legalDescriptions.push(trimmed);
          }
        }
        continue;
      }

      const bookPage = parts[1] || '';
      
      // Parse the role markers and names from remaining columns
      // Format varies: could be [2, name, 1, name, docType, date] or [1, name, 2, name, docType, date]
      let grantor = '';
      let grantee = '';
      let docType = '';
      let recordedDate = '';
      
      // Find the columns - scan for "1", "2", doc type, and date
      const remaining = parts.slice(2);
      let currentRole: '1' | '2' | null = null;
      const names: { role: string; name: string }[] = [];
      
      for (let i = 0; i < remaining.length; i++) {
        const val = remaining[i];
        if (val === '1' || val === '2') {
          currentRole = val;
          continue;
        }
        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(val)) {
          recordedDate = val;
          continue;
        }
        if (/^(DEED OF TRUST|VENDOR|CONTRACT FOR DEED|LAND CONTRACT|WARRANTY DEED|QUIT CLAIM|ASSIGNMENT|RELEASE)/i.test(val)) {
          docType = val;
          continue;
        }
        if (val.startsWith('L:')) {
          // Legal description inline
          if (!documentMap.has(docNum)) {
            documentMap.set(docNum, {
              documentNumber: docNum, bookPage, grantors: [], grantees: [],
              docType, recordedDate, legalDescriptions: [val], lenderName: '',
            });
          } else {
            documentMap.get(docNum)!.legalDescriptions.push(val);
          }
          continue;
        }
        if (val === '' || val === 'Verified') continue;
        
        // This is a name
        if (currentRole) {
          names.push({ role: currentRole, name: val.replace(/\s*\+\s*$/, '').trim() });
          currentRole = null;
        }
      }

      if (!documentMap.has(docNum)) {
        documentMap.set(docNum, {
          documentNumber: docNum,
          bookPage: bookPage.replace(/\s*\+\s*$/, '').trim(),
          grantors: [],
          grantees: [],
          docType,
          recordedDate,
          legalDescriptions: [],
          lenderName: '',
        });
      }

      const doc = documentMap.get(docNum)!;
      if (!doc.docType && docType) doc.docType = docType;
      if (!doc.recordedDate && recordedDate) doc.recordedDate = recordedDate;

      for (const { role, name } of names) {
        if (!name) continue;
        if (role === '1') {
          if (!doc.grantors.includes(name)) doc.grantors.push(name);
        } else if (role === '2') {
          if (!doc.grantees.includes(name)) doc.grantees.push(name);
        }
      }
    }

    return finalizeDocs(documentMap);
  };

  const parseVerticalFormat = (lines: string[]): BrewsterDocument[] => {
    const documentMap = new Map<string, BrewsterDocument>();
    let currentDocNum = '';
    let currentBookPage = '';
    let currentRole: '1' | '2' | null = null;
    let pendingNames: { role: string; name: string }[] = [];
    let currentDocType = '';
    let currentDate = '';
    let currentLegals: string[] = [];

    const flushDoc = () => {
      if (!currentDocNum) return;
      
      if (!documentMap.has(currentDocNum)) {
        documentMap.set(currentDocNum, {
          documentNumber: currentDocNum,
          bookPage: currentBookPage,
          grantors: [],
          grantees: [],
          docType: currentDocType,
          recordedDate: currentDate,
          legalDescriptions: [...currentLegals],
          lenderName: '',
        });
      }

      const doc = documentMap.get(currentDocNum)!;
      if (!doc.docType && currentDocType) doc.docType = currentDocType;
      if (!doc.recordedDate && currentDate) doc.recordedDate = currentDate;
      for (const legal of currentLegals) {
        if (!doc.legalDescriptions.includes(legal)) doc.legalDescriptions.push(legal);
      }

      for (const { role, name } of pendingNames) {
        if (role === '1') {
          if (!doc.grantors.includes(name)) doc.grantors.push(name);
        } else if (role === '2') {
          if (!doc.grantees.includes(name)) doc.grantees.push(name);
        }
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line || line.startsWith('Doc#') || line === 'Verified') continue;

      // Doc number line: 5-7 digits alone
      if (/^\d{5,7}$/.test(line)) {
        flushDoc();
        currentDocNum = line;
        currentBookPage = '';
        currentRole = null;
        pendingNames = [];
        currentDocType = '';
        currentDate = '';
        currentLegals = [];
        continue;
      }

      // Book/page line: OPR xxxx xxxx
      if (/^OPR\s+\d{4}\s+\d{4}/i.test(line)) {
        currentBookPage = line.replace(/\s*\+\s*$/, '').trim();
        continue;
      }

      // Role marker: standalone "1" or "2"
      if (line === '1' || line === '2') {
        currentRole = line as '1' | '2';
        continue;
      }

      // Legal description
      if (line.startsWith('L:')) {
        currentLegals.push(line);
        continue;
      }

      // Date line (standalone)
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(line)) {
        currentDate = line;
        continue;
      }

      // Doc type line
      if (/^(DEED OF TRUST|VENDOR|CONTRACT FOR DEED|LAND CONTRACT|WARRANTY DEED|SPECIAL WARRANTY|QUIT CLAIM|ASSIGNMENT|RELEASE)/i.test(line)) {
        // Might have date appended with spaces/tabs
        const dateMatch = line.match(/(\d{1,2}\/\d{1,2}\/\d{4})\s*$/);
        if (dateMatch) {
          currentDate = dateMatch[1];
          currentDocType = line.substring(0, line.indexOf(dateMatch[1])).trim();
        } else {
          currentDocType = line;
        }
        continue;
      }

      // If we have a current role, this line is a name
      if (currentRole && currentDocNum) {
        const cleanName = line.replace(/\s*\+\s*$/, '').trim();
        if (cleanName.length > 0) {
          pendingNames.push({ role: currentRole, name: cleanName });
        }
        // Don't reset currentRole - multiple names can be under same role
        // Role resets when we see the other role number
        continue;
      }
    }

    // Flush last document
    flushDoc();

    return finalizeDocs(documentMap);
  };

  const finalizeDocs = (documentMap: Map<string, BrewsterDocument>): BrewsterDocument[] => {
    const docs = Array.from(documentMap.values());

    // Identify primary lender for each doc
    for (const doc of docs) {
      for (const grantee of doc.grantees) {
        const upper = grantee.toUpperCase();
        // Skip trustees
        if (upper.includes('TRUSTEE') || upper.includes(', TR') || upper.includes(' TR ') || upper.endsWith(' TR')) continue;
        if (upper.includes('POLUNSKY') || upper.includes('SANDLER LAW') || upper.includes('MARINOSCI')) continue;
        if (!doc.lenderName) {
          doc.lenderName = grantee;
        }
      }
      // Fallback: use first grantee
      if (!doc.lenderName && doc.grantees.length > 0) {
        doc.lenderName = doc.grantees[0];
      }
    }

    // Sort by date descending (newest first)
    docs.sort((a, b) => {
      const parseDate = (d: string) => {
        if (!d) return 0;
        const parts = d.split('/');
        if (parts.length === 3) return new Date(`${parts[2]}-${parts[0].padStart(2,'0')}-${parts[1].padStart(2,'0')}`).getTime();
        return 0;
      };
      return parseDate(b.recordedDate) - parseDate(a.recordedDate);
    });

    return docs;
  };

  const isCorpLender = (name: string): boolean => {
    const upper = name.toUpperCase();
    return CORPORATE_KEYWORDS.some(kw => upper.includes(kw));
  };

  const isFamilyTransfer = (doc: BrewsterDocument): boolean => {
    const getLastName = (name: string) => name.split(/\s+/)[0]?.toUpperCase() || '';
    const grantorLastNames = doc.grantors.map(getLastName).filter(n => n.length > 1);
    const granteeLastNames = doc.grantees.map(getLastName).filter(n => n.length > 1);
    return grantorLastNames.some(gln => granteeLastNames.some(eln => gln === eln));
  };

  const handleProcess = async () => {
    if (!textData.trim()) {
      toast({ title: "No data", description: "Please paste records", variant: "destructive" });
      return;
    }

    setIsProcessing(true);
    try {
      const documents = parseBrewsterData(textData);
      console.log(`Parsed ${documents.length} total documents`);
      
      if (documents.length === 0) {
        toast({ title: "No records found", description: "Could not parse any records from the pasted data.", variant: "destructive" });
        setIsProcessing(false);
        return;
      }

      // Only keep relevant doc types
      const deedDocs = documents.filter(d => {
        const upper = d.docType.toUpperCase();
        return upper.includes('DEED OF TRUST') || upper.includes('VENDOR') ||
               upper.includes('CONTRACT FOR DEED') || upper.includes('LAND CONTRACT');
      });

      const familyCount = deedDocs.filter(isFamilyTransfer).length;
      const nonFamily = deedDocs.filter(d => !isFamilyTransfer(d));

      const corporateCount = nonFamily.filter(d => d.lenderName && isCorpLender(d.lenderName)).length;
      const potentialOwnerFinanced = nonFamily.filter(d => {
        if (!d.lenderName) return false;
        return !isCorpLender(d.lenderName);
      });

      console.log(`Deeds: ${deedDocs.length}, Family: ${familyCount}, Corporate: ${corporateCount}, Potential OF: ${potentialOwnerFinanced.length}`);

      if (potentialOwnerFinanced.length === 0) {
        toast({
          title: "No owner-financed deals found",
          description: `Analyzed ${documents.length} documents — ${deedDocs.length} deeds, ${familyCount} family transfers, ${corporateCount} corporate lenders`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Send to AI for final verification
      const deedText = potentialOwnerFinanced.map(d =>
        `Document: ${d.documentNumber}\n` +
        `Date: ${d.recordedDate}\n` +
        `Grantor(s): ${d.grantors.join(', ')}\n` +
        `Grantee/Beneficiary: ${d.lenderName}\n` +
        `All Grantees: ${d.grantees.join(', ')}\n` +
        `Legal: ${d.legalDescriptions.join(' | ')}\n`
      ).join('\n---\n');

      const { data: filterData, error: filterError } = await supabase.functions.invoke(
        'filter-owner-finance',
        { body: { deedText } }
      );

      if (filterError) throw filterError;

      const ownerFinancedDeals = filterData?.filteredDeals || [];
      const ownerFinancedDocNumbers = new Set(
        ownerFinancedDeals.map((deal: any) => deal.documentNumber)
      );

      const confirmedRecords = potentialOwnerFinanced.filter(d =>
        ownerFinancedDocNumbers.has(d.documentNumber)
      );

      console.log(`AI confirmed ${confirmedRecords.length} owner-financed deals`);

      if (confirmedRecords.length === 0) {
        toast({
          title: "No owner-financed deals confirmed",
          description: `Hard filter passed ${potentialOwnerFinanced.length}, but AI rejected all as non-individual lenders`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Check for existing records
      const existingQuery = await supabase
        .from('county_research')
        .select('document_number')
        .eq('county', 'brewster')
        .in('document_number', confirmedRecords.map(r => r.documentNumber));

      const existingDocs = new Set(existingQuery.data?.map(d => d.document_number) || []);
      const newRecords = confirmedRecords.filter(r => !existingDocs.has(r.documentNumber));

      if (newRecords.length === 0) {
        toast({
          title: "No new records",
          description: `Found ${confirmedRecords.length} owner-financed deals, but all already exist in database`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      const parseDate = (dateStr: string): string => {
        if (!dateStr) return new Date().toISOString().split('T')[0];
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
        }
        return new Date().toISOString().split('T')[0];
      };

      // For Deeds of Trust: Grantor = Borrower, Grantee/Beneficiary = Lender
      // Swap so that grantor_name (displayed as "Seller") = Lender/Beneficiary
      // and grantee_name (displayed as "Buyer") = Borrower/Grantor
      const recordsToInsert = newRecords.map(record => ({
        county: 'brewster',
        document_number: record.documentNumber,
        filing_date: parseDate(record.recordedDate),
        document_type: record.docType,
        grantor_name: record.grantees.join('; '),
        grantee_name: record.grantors.join('; '),
        legal_description: record.legalDescriptions.join('\n'),
        is_potential_owner_finance: true,
        confidence_score: 80,
        detection_reasons: ['AI confirmed individual beneficiary'],
        status: 'new',
        raw_data: {
          bookPage: record.bookPage,
          grantors: record.grantors,
          grantees: record.grantees,
        } as any,
      }));

      const { error: insertError } = await supabase
        .from('county_research')
        .upsert(recordsToInsert, { onConflict: 'document_number', ignoreDuplicates: false });

      if (insertError) throw insertError;

      backupRecordsToExcel(recordsToInsert, 'brewster', 'brewster_parser');

      toast({
        title: "Success!",
        description: `Saved ${newRecords.length} owner-financed deals!\n` +
          `Total: ${documents.length} | Deeds: ${deedDocs.length} | Family: ${familyCount} | Corporate: ${corporateCount} | ` +
          `AI confirmed: ${confirmedRecords.length} | New saved: ${newRecords.length}`,
      });

      setTextData("");
      onRecordsSaved();
    } catch (error) {
      console.error('Error processing Brewster County data:', error);
      toast({ title: "Error", description: "Failed to process records. Please try again.", variant: "destructive" });
    } finally {
      setIsProcessing(false);
    }
  };

  const previewCount = textData.trim() ? parseBrewsterData(textData).length : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Brewster County, TX - Text Parser with AI Filtering</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Paste deed of trust records from the Brewster County Public Records search.
            Supports both tab-separated table format and vertical copy-paste format.
          </p>
          <p className="text-sm text-muted-foreground">
            <strong>Markers:</strong> "1" = Grantor (borrower), "2" = Grantee (lender).
            Records are filtered to remove corporate lenders and family transfers, then AI-verified.
          </p>
        </div>

        <Textarea
          value={textData}
          onChange={(e) => setTextData(e.target.value)}
          placeholder={`Paste Brewster County deed of trust records here...\n\nExample format:\n122512\nOPR 0473 0792\n1\nRICHARDSON DOROTHY MALYNDA\n2\nBEGGS KRISTEN D\nDEED OF TRUST\n11/05/2025\nL: 6 B: 31 C: ALPINE A: ORIGINAL TOWNSITE OF ALPINE`}
          className="min-h-[300px] font-mono text-sm"
        />

        {previewCount > 0 && (
          <p className="text-sm text-muted-foreground">
            Detected <strong>{previewCount}</strong> unique documents to analyze
          </p>
        )}

        <div className="flex gap-2">
          <Button
            onClick={handleProcess}
            disabled={isProcessing || !textData.trim()}
            className="flex-1"
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing with AI...
              </>
            ) : (
              `Process ${previewCount > 0 ? previewCount + ' ' : ''}Records`
            )}
          </Button>

          <CleanupFamilyTransfersButton onCleanupComplete={onRecordsSaved} />
        </div>

        {isProcessing && (
          <p className="text-sm text-muted-foreground text-center">
            Filtering corporate lenders & family transfers, then AI verifying remaining records...
          </p>
        )}
      </CardContent>
    </Card>
  );
}
